import { LoginRequestBlock } from '@/components/blocks/LoginRequestBlock';
import { AddAddressDrawer } from '@/components/content/CheckoutProfiles/parts/CreateEdit/parts/AddAddressDrawer';
import { EditAddressDrawer } from '@/components/content/CheckoutProfiles/parts/CreateEdit/parts/EditAddressDrawer';
import { useDeliveryLocationCookie } from '@/components/content/Header/parts/DeliveryLocator/hooks/useDeliveryLocationCookie';
import { useDeliveryLocator } from '@/components/content/Header/parts/DeliveryLocator/hooks/useDeliveryLocator';
import { useZipCodeInput } from '@/components/content/Header/parts/DeliveryLocator/hooks/useZipCodeInput';
import { DeliveryLocatorHeaderInfo } from '@/components/content/Header/parts/DeliveryLocator/parts/DeliveryLocatorHeaderInfo/DeliveryLocatorHeaderInfo';
import { GeolocalizationRequestView } from '@/components/content/Header/parts/DeliveryLocator/parts/GeolocalizationRequestView/GeolocalizationRequestView';
import { DeliveryLocatorPanelTitle } from '@/components/content/Header/parts/DeliveryLocator/parts/PanelTitle/PanelTitle';
import { SavedAddresses } from '@/components/content/Header/parts/DeliveryLocator/parts/SavedAddresses/SavedAddresses';
import { SelectedLocationBox } from '@/components/content/Header/parts/DeliveryLocator/parts/SelectedLocationBox/SelectedLocationBox';
import { ZipCodeInput } from '@/components/content/Header/parts/DeliveryLocator/parts/ZipCodeInput/ZipCodeInput';
import {
	blockedZipCodeWarningSX,
	deliveryLocatorContentContainerSX,
	deliveryLocatorContentDividerSX,
	deliveryLocatorIntroTextSX,
} from '@/components/content/Header/parts/DeliveryLocator/styles/HeaderDeliveryLocator/headerDeliveryLocatorStyles';
import { HeaderMenuPanel } from '@/components/content/Header/parts/HeaderMenuPanel';
import { LocationAlert } from '@/components/content/Header/parts/LocationAlert';
import { useLocationAlert } from '@/components/content/Header/parts/LocationAlert/hooks/useLocationAlert';
import { useAddressBook } from '@/data/Content/AddressBook';
import { ContentProvider } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { useUser } from '@/data/User';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';

export const HeaderDeliveryLocator = () => {
	const localization = useLocalization('DeliveryLocator');
	const { user } = useUser();
	const userEmail = user?.email || '';
	const isLoggedIn = !!user?.isLoggedIn;

	const {
		addressList,
		handleCloseAddAddress,
		handleCloseDeliveryPanel,
		handleCloseEditAddress,
		handleOpenAddAddress,
		handleOpenDeliveryPanel,
		handleOpenEditAddress,
		isAddAddressOpen,
		isDeliveryPanelOpen,
		isEditAddressOpen,
		openGeolocalizationRequestView,
		selectedEditAddress,
		setOpenGeolocalizationRequestView,
		setZipCodeInputValue,
		stackRef,
		zipCodeInputValue,
	} = useDeliveryLocator();

	const { addressId, locality, state, zipCode } = useDeliveryLocationCookie();
	const { blockedZipCodes } = useAddressBook();
	const isInDelimitedDeliveryZone = useMemo(() => {
		const isSelectedBlocked = blockedZipCodes?.includes(zipCode);
		const isInputBlocked = blockedZipCodes?.includes(zipCodeInputValue);
		if (!isSelectedBlocked) return isInputBlocked;
		if (zipCodeInputValue.length === 5) return isInputBlocked;
		return true;
	}, [blockedZipCodes, zipCode, zipCodeInputValue]);

	const selectedAddress = addressList.find((address) => address.addressId === addressId);

	const zipCodeInputValues = useZipCodeInput({
		zipCodeInputValue,
		setZipCodeInputValue,
		handleClose: handleCloseDeliveryPanel,
	});

	const overrideHandleOpen = () => {
		zipCodeInputValues.resetZipCodeError();
		handleOpenDeliveryPanel();
	};
	const hasAddresses = !!addressList[0]?.zipCode;
	const { isVisible: isLocationAlertVisible, dismiss: dismissLocationAlert } =
		useLocationAlert('location');

	useEffect(() => {
		if (isDeliveryPanelOpen) dismissLocationAlert();
	}, [isDeliveryPanelOpen, dismissLocationAlert]);

	const overrideHandleClose = () => {
		zipCodeInputValues.setFocused(false);
		handleCloseDeliveryPanel();
	};

	const children = (
		<>
			<DeliveryLocatorPanelTitle handleClose={handleCloseDeliveryPanel} />
			<Divider />
			<Stack sx={deliveryLocatorContentContainerSX}>
				{openGeolocalizationRequestView ? (
					<GeolocalizationRequestView
						setOpenGeolocalizationRequestView={setOpenGeolocalizationRequestView}
					/>
				) : (
					<>
						{hasAddresses ? null : (
							<Typography variant="bodySubText" sx={deliveryLocatorIntroTextSX}>
								{localization.introText.t()}
							</Typography>
						)}
						<SelectedLocationBox
							selectedAddress={selectedAddress}
							locality={locality}
							state={state}
							zipCode={zipCode}
							userEmail={userEmail}
						/>
						{isInDelimitedDeliveryZone ? (
							<Box sx={blockedZipCodeWarningSX}>
								<Typography className="delivery-location__alert-message" variant="bodySubText">
									{localization.Msgs.blockedZipCode.t()}
								</Typography>
							</Box>
						) : null}
						{isLoggedIn ? (
							<>
								<SavedAddresses
									addressList={addressList}
									selectedAddress={selectedAddress}
									handleOnEditAddress={handleOpenEditAddress}
									handleOpenAddAddressDrawer={handleOpenAddAddress}
									handleCloseDeliveryPanel={handleCloseDeliveryPanel}
								/>
								<Divider sx={deliveryLocatorContentDividerSX} />
							</>
						) : null}
						<ZipCodeInput
							zipCodeInputValue={zipCodeInputValue}
							setOpenGeolocalizationRequestView={setOpenGeolocalizationRequestView}
						/>
						{!isLoggedIn ? (
							<>
								<Divider sx={deliveryLocatorContentDividerSX} />
								<LoginRequestBlock
									panelText={localization.Msgs.loginRequest.t()}
									signInId="loginLocationPanel"
									registerId="newAccountLocationPanel"
									onActionClick={handleCloseDeliveryPanel}
								/>
							</>
						) : null}
					</>
				)}
			</Stack>
		</>
	);

	return (
		<ContentProvider value={{ ...zipCodeInputValues }}>
			<Stack id="locationSelectorBlock" ref={stackRef} onClick={overrideHandleOpen}>
				<DeliveryLocatorHeaderInfo
					isLocatorOptionSelected={isDeliveryPanelOpen}
					zipCode={zipCode}
				/>
			</Stack>
			<LocationAlert
				variant="location"
				anchorEl={stackRef.current}
				isVisible={isLocationAlertVisible}
				onAction={overrideHandleOpen}
				onDismiss={dismissLocationAlert}
			/>
			<HeaderMenuPanel
				id="locationPanel"
				isOpen={isDeliveryPanelOpen}
				handleClose={overrideHandleClose}
				anchorEl={stackRef.current}
			>
				{isAddAddressOpen ? (
					<AddAddressDrawer
						isOpen={isAddAddressOpen}
						onClose={handleCloseAddAddress}
						onSuccess={overrideHandleOpen}
						showNoDrawer
					/>
				) : isEditAddressOpen ? (
					<EditAddressDrawer
						isOpen={isEditAddressOpen}
						onClose={handleCloseEditAddress}
						onSuccess={overrideHandleOpen}
						initialValues={selectedEditAddress}
						showNoDrawer
					/>
				) : (
					children
				)}
			</HeaderMenuPanel>
		</ContentProvider>
	);
};
